import { StrictMode } from "react";
import "./bootstrap/immer";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { toast } from "sonner";

// Global error handlers to catch and log errors
window.addEventListener("error", (event) => {
  // Known benign dev-time noise; ignore to keep console readable
  if (
    event.message.includes("ResizeObserver loop completed with undelivered notifications") ||
    event.message.includes("There was an error during concurrent rendering but React was able to recover by instead synchronously rendering the entire root.")
  ) {
    event.preventDefault();
    return;
  }
  console.error("Global error caught:", {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error,
    stack: (event as any).error?.stack,
  });

  // Surface a user-visible notification as well
  try {
    const location = [event.filename, event.lineno, event.colno]
      .filter((v) => v !== undefined && v !== null && v !== "")
      .join(":");
    const description = location ? `at ${location}` : undefined;
    toast.error(event.message || "Unexpected error", description ? { description: "YOO TESTING", duration: 10_000 } : { duration: 10_000 });
  } catch { }
});

window.addEventListener("unhandledrejection", (event) => {
  console.error("Unhandled promise rejection:", {
    reason: event.reason,
    promise: event.promise,
  });

  // Notify via toaster for visibility
  try {
    const reason = event.reason as any;
    if (reason && typeof reason === 'object' && (reason as any).__toasted) return;

    // Prefer server-provided title when available
    const data = reason?.response?.data ?? reason?.data;
    const serverCode = data?.error?.code || data?.code;
    const serverMsg = data?.error?.message || data?.message;
    const baseTitle = typeof reason === 'string'
      ? reason
      : (reason?.friendlyMessage || (serverMsg ? (serverCode ? `${serverMsg} (${serverCode})` : serverMsg) : reason?.message) || 'Unhandled error');

    // Extract detailed description from common server error shapes
    const details = data?.details ?? data?.error?.details ?? undefined;
    let description: string | undefined;

    if (typeof details?.error === 'string') {
      description = details.error;
    } else if (typeof details === 'string') {
      description = details;
    } else if (typeof data?.error?.details?.error === 'string') {
      description = data.error.details.error as string;
    } else if (typeof data?.error?.details === 'string') {
      description = data.error.details as string;
    } else if (typeof data?.error === 'string') {
      description = data.error as string;
    } else if (typeof reason?.friendlyDetails === 'string') {
      description = reason.friendlyDetails as string;
    }

    // Fallback: derive description from multiline message if details missing
    if (!description && typeof reason?.message === 'string' && reason.message.includes('\n')) {
      const idx = reason.message.indexOf('\n');
      const tail = reason.message.slice(idx + 1).trim();
      if (tail) description = tail;
    }

    let shownTitle = baseTitle as string;
    if (description && typeof shownTitle === 'string' && shownTitle.includes(description)) {
      shownTitle = shownTitle.replace(description, '').replace(/[:\s]*$/, '').trim();
    }
    toast.error(shownTitle, { description, duration: 10_000 });
  } catch { }
});

const elem = document.getElementById("root")!;
const app = (
  <StrictMode>
    <App />
  </StrictMode>
);

if (import.meta.hot) {
  const root = (import.meta.hot.data.root ??= createRoot(elem));
  root.render(app);
} else {
  createRoot(elem).render(app);
}
